/**
 * Pause Menu Manager
 * Unified pause menu that handles ESC key, settings, and game exit
 */

import { frontendLogger } from '../../shared/logger';

export type PauseMenuAction = 'resume' | 'settings' | 'quit' | 'mainmenu';

export interface PauseMenuOptions {
  onResume?: () => void;
  onSettings?: () => void;
  onQuit?: () => void;
  onMainMenu?: () => void;
}

export class PauseMenu {
  private pauseMenuElement?: HTMLElement;
  private isVisible: boolean = false;
  private escapeHandler?: (e: KeyboardEvent) => void;
  private options: PauseMenuOptions;

  constructor(options: PauseMenuOptions = {}) {
    this.options = options;
    this.createPauseMenu();
    this.setupEscapeHandler();
  }

  private createPauseMenu(): void {
    // Create pause menu container
    this.pauseMenuElement = document.createElement('div');
    this.pauseMenuElement.id = 'pause-menu';
    this.pauseMenuElement.className = 'pause-menu';
    this.pauseMenuElement.innerHTML = `
      <div class="pause-menu-content">
        <h2 class="pause-menu-title">GAME PAUSED</h2>
        
        <div class="pause-menu-buttons">
          <button id="pause-resume" class="pause-menu-button">
            <span class="button-icon">▶</span>
            <span>Resume Game</span>
          </button>
          
          <button id="pause-settings" class="pause-menu-button">
            <span class="button-icon">⚙</span>
            <span>Settings</span>
          </button>
          
          <button id="pause-mainmenu" class="pause-menu-button">
            <span class="button-icon">🏠</span>
            <span>Main Menu</span>
          </button>
          
          <button id="pause-quit" class="pause-menu-button pause-menu-button-danger">
            <span class="button-icon">✖</span>
            <span>Quit Game</span>
          </button>
        </div>
        
        <div class="pause-menu-footer">
          Press ESC to resume
        </div>
      </div>
    `;

    // Add styles
    const style = document.createElement('style');
    style.textContent = `
      .pause-menu {
        position: fixed;
        inset: 0;
        background: rgba(0, 0, 0, 0.85);
        backdrop-filter: blur(10px);
        display: none;
        align-items: center;
        justify-content: center;
        z-index: 9998;
        animation: fadeIn 0.2s ease-out;
      }
      
      .pause-menu.visible {
        display: flex;
      }
      
      @keyframes fadeIn {
        from { opacity: 0; }
        to { opacity: 1; }
      }
      
      .pause-menu-content {
        background: linear-gradient(135deg, #1a1f2e 0%, #2a2f3e 100%);
        border: 2px solid rgba(255, 165, 0, 0.3);
        border-radius: 16px;
        padding: 40px;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.8);
        max-width: 400px;
        width: 90%;
        animation: slideUp 0.3s ease-out;
      }
      
      @keyframes slideUp {
        from { transform: translateY(20px); opacity: 0; }
        to { transform: translateY(0); opacity: 1; }
      }
      
      .pause-menu-title {
        text-align: center;
        font-size: 32px;
        font-weight: bold;
        color: #ffa500;
        margin-bottom: 30px;
        text-transform: uppercase;
        letter-spacing: 0.15em;
        text-shadow: 0 2px 10px rgba(255, 165, 0, 0.5);
      }
      
      .pause-menu-buttons {
        display: flex;
        flex-direction: column;
        gap: 12px;
      }
      
      .pause-menu-button {
        display: flex;
        align-items: center;
        gap: 15px;
        padding: 16px 24px;
        background: rgba(255, 255, 255, 0.05);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 10px;
        color: white;
        font-size: 18px;
        font-weight: 500;
        cursor: pointer;
        transition: all 0.3s ease;
        text-align: left;
      }
      
      .pause-menu-button:hover {
        background: rgba(255, 165, 0, 0.2);
        border-color: rgba(255, 165, 0, 0.5);
        transform: translateX(5px);
        box-shadow: 0 5px 20px rgba(255, 165, 0, 0.3);
      }
      
      .pause-menu-button:active {
        transform: translateX(3px) scale(0.98);
      }
      
      .pause-menu-button-danger {
        border-color: rgba(255, 67, 67, 0.3);
      }
      
      .pause-menu-button-danger:hover {
        background: rgba(255, 67, 67, 0.2);
        border-color: rgba(255, 67, 67, 0.5);
        box-shadow: 0 5px 20px rgba(255, 67, 67, 0.3);
      }
      
      .button-icon {
        font-size: 20px;
        width: 24px;
        text-align: center;
      }
      
      .pause-menu-footer {
        margin-top: 25px;
        text-align: center;
        color: rgba(255, 255, 255, 0.5);
        font-size: 14px;
        font-style: italic;
      }
      
      /* Settings Panel (initially hidden) */
      .pause-settings-panel {
        display: none;
      }
      
      .pause-settings-panel.active {
        display: block;
      }
      
      .settings-group {
        margin-bottom: 20px;
      }
      
      .settings-group label {
        display: block;
        color: #ffa500;
        margin-bottom: 8px;
        font-size: 14px;
        text-transform: uppercase;
        letter-spacing: 0.05em;
      }
      
      .settings-slider {
        width: 100%;
        margin: 10px 0;
      }
      
      .settings-back-button {
        margin-top: 20px;
        width: 100%;
        padding: 12px;
        background: rgba(255, 255, 255, 0.1);
        border: 2px solid rgba(255, 255, 255, 0.2);
        border-radius: 8px;
        color: white;
        cursor: pointer;
        transition: all 0.3s ease;
      }
      
      .settings-back-button:hover {
        background: rgba(255, 255, 255, 0.15);
        border-color: rgba(255, 255, 255, 0.3);
      }
    `;
    document.head.appendChild(style);
    document.body.appendChild(this.pauseMenuElement);

    // Setup button handlers
    this.setupButtonHandlers();
  }

  private setupButtonHandlers(): void {
    // Resume button
    const resumeBtn = document.getElementById('pause-resume');
    if (resumeBtn) {
      resumeBtn.addEventListener('click', () => {
        this.hide();
        this.options.onResume?.();
      });
    }

    // Settings button
    const settingsBtn = document.getElementById('pause-settings');
    if (settingsBtn) {
      settingsBtn.addEventListener('click', () => {
        this.showSettings();
        this.options.onSettings?.();
      });
    }

    // Main Menu button
    const mainMenuBtn = document.getElementById('pause-mainmenu');
    if (mainMenuBtn) {
      mainMenuBtn.addEventListener('click', () => {
        this.hide();
        this.options.onMainMenu?.();
      });
    }

    // Quit button
    const quitBtn = document.getElementById('pause-quit');
    if (quitBtn) {
      quitBtn.addEventListener('click', () => {
        if (confirm('Are you sure you want to quit the game?')) {
          this.hide();
          this.options.onQuit?.();
        }
      });
    }
  }

  private setupEscapeHandler(): void {
    this.escapeHandler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        e.stopPropagation();
        this.toggle();
      }
    };
  }

  private showSettings(): void {
    // Create settings panel if it doesn't exist
    if (!this.pauseMenuElement) return;
    
    const content = this.pauseMenuElement.querySelector('.pause-menu-content');
    if (!content) return;

    content.innerHTML = `
      <h2 class="pause-menu-title">SETTINGS</h2>
      
      <div class="pause-settings-panel active">
        <div class="settings-group">
          <label>Master Volume: <span id="volume-display">70%</span></label>
          <input type="range" class="settings-slider" id="settings-volume" min="0" max="100" value="70">
        </div>
        
        <div class="settings-group">
          <label>Mouse Sensitivity: <span id="sensitivity-display">50%</span></label>
          <input type="range" class="settings-slider" id="settings-sensitivity" min="10" max="200" value="50">
        </div>
        
        <div class="settings-group">
          <label>Graphics Quality</label>
          <select id="settings-graphics" style="width: 100%; padding: 8px; background: rgba(0,0,0,0.5); color: white; border: 1px solid rgba(255,255,255,0.2); border-radius: 4px;">
            <option value="low">Low</option>
            <option value="medium" selected>Medium</option>
            <option value="high">High</option>
          </select>
        </div>
        
        <div class="settings-group">
          <label>
            <input type="checkbox" id="settings-fullscreen"> Fullscreen
          </label>
        </div>
        
        <button class="settings-back-button" id="settings-back">← Back to Pause Menu</button>
      </div>
    `;

    // Setup settings handlers
    const volumeSlider = document.getElementById('settings-volume') as HTMLInputElement;
    const volumeDisplay = document.getElementById('volume-display');
    if (volumeSlider && volumeDisplay) {
      volumeSlider.addEventListener('input', (e) => {
        const value = (e.target as HTMLInputElement).value;
        volumeDisplay.textContent = `${value}%`;
        // TODO: Apply volume change
      });
    }

    const sensitivitySlider = document.getElementById('settings-sensitivity') as HTMLInputElement;
    const sensitivityDisplay = document.getElementById('sensitivity-display');
    if (sensitivitySlider && sensitivityDisplay) {
      sensitivitySlider.addEventListener('input', (e) => {
        const value = (e.target as HTMLInputElement).value;
        sensitivityDisplay.textContent = `${value}%`;
        // TODO: Apply sensitivity change
      });
    }

    const backBtn = document.getElementById('settings-back');
    if (backBtn) {
      backBtn.addEventListener('click', () => {
        this.createPauseMenu(); // Recreate main pause menu
        this.setupButtonHandlers();
        this.isVisible = true; // Keep it visible
        if (this.pauseMenuElement) {
          this.pauseMenuElement.classList.add('visible');
        }
      });
    }
  }

  public show(): void {
    if (this.pauseMenuElement && !this.isVisible) {
      this.pauseMenuElement.classList.add('visible');
      this.isVisible = true;
      
      // Release pointer lock
      if (document.pointerLockElement) {
        document.exitPointerLock();
      }
      
      frontendLogger.info('Pause menu shown');
    }
  }

  public hide(): void {
    if (this.pauseMenuElement && this.isVisible) {
      this.pauseMenuElement.classList.remove('visible');
      this.isVisible = false;
      frontendLogger.info('Pause menu hidden');
    }
  }

  public toggle(): void {
    if (this.isVisible) {
      this.hide();
      this.options.onResume?.();
    } else {
      this.show();
    }
  }

  public isOpen(): boolean {
    return this.isVisible;
  }

  public enable(): void {
    if (this.escapeHandler) {
      document.addEventListener('keydown', this.escapeHandler);
    }
  }

  public disable(): void {
    if (this.escapeHandler) {
      document.removeEventListener('keydown', this.escapeHandler);
    }
  }

  public destroy(): void {
    this.disable();
    if (this.pauseMenuElement && this.pauseMenuElement.parentNode) {
      this.pauseMenuElement.parentNode.removeChild(this.pauseMenuElement);
    }
  }
}
